import { SAVE_THE_DATE_LABEL } from '../../../services/events/eventEditorialPick';
import {
  addCalendarDays,
  calendarDayKey,
  EVENT_LOAD_DAY_WINDOW,
  getEventCalendarDay,
  startOfLocalDay,
} from '../../../services/dateUtils';
import { countLoadedByDay, parseCalendarDayKey, prependUniqueById } from './eventFeedLoader';

export const EVENT_FEED_CACHE_KEY = 'm2b-public-event-feed-v1';

/** @type {object|null} */
let memorySnapshot = null;

export function updatedAtMillis(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value.toMillis === 'function') {
    const ms = value.toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  if (value && typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanos / 1e6);
  }
  return null;
}

function normalizeAggregate(value) {
  if (!value || typeof value !== 'object') return null;
  if (typeof value.upcomingCount !== 'number') return null;
  return {
    upcomingCount: value.upcomingCount,
    byDay: value.byDay && typeof value.byDay === 'object' ? value.byDay : {},
    byCategory: value.byCategory && typeof value.byCategory === 'object' ? value.byCategory : {},
    tags: Array.isArray(value.tags) ? value.tags : [],
    updatedAt: updatedAtMillis(value.updatedAt),
  };
}

function normalizeSnapshot(value) {
  if (!value || typeof value !== 'object') return null;
  if (!value.dayKey || typeof value.dayKey !== 'string') return null;
  if (!Array.isArray(value.events)) return null;
  const aggregate = normalizeAggregate(value.aggregate);
  if (!aggregate) return null;
  return {
    at: typeof value.at === 'number' ? value.at : Date.now(),
    dayKey: value.dayKey,
    aggregate,
    featured: Array.isArray(value.featured) ? value.featured : [],
    events: value.events,
    completedDayKeys: Array.isArray(value.completedDayKeys) ? value.completedDayKeys : [],
    loadedThrough: typeof value.loadedThrough === 'string' ? value.loadedThrough : '',
  };
}

function readSession() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return normalizeSnapshot(JSON.parse(sessionStorage.getItem(EVENT_FEED_CACHE_KEY)));
  } catch {
    return null;
  }
}

function toSessionSlice(snapshot) {
  const today = parseCalendarDayKey(snapshot.dayKey);
  const firstEnd = today ? addCalendarDays(today, EVENT_LOAD_DAY_WINDOW) : null;
  const featured = snapshot.featured;
  const featuredIds = new Set(featured.map((event) => event?.id).filter(Boolean));
  const windowEvents = snapshot.events.filter((event) => {
    if (featuredIds.has(event?.id)) return true;
    if (!today || !firstEnd) return false;
    const day = getEventCalendarDay(event);
    return Boolean(day && day >= today && day < firstEnd);
  });
  const events = prependUniqueById(featured, windowEvents);
  const completedDayKeys = snapshot.completedDayKeys.filter((key) => {
    const day = parseCalendarDayKey(key);
    return Boolean(today && firstEnd && day && day >= today && day < firstEnd);
  });
  return {
    ...snapshot,
    featured,
    events,
    completedDayKeys,
    loadedThrough: firstEnd ? calendarDayKey(firstEnd) : snapshot.loadedThrough,
  };
}

export function clearEventFeedCache() {
  memorySnapshot = null;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(EVENT_FEED_CACHE_KEY);
    }
  } catch {
    /* private mode */
  }
}

/**
 * Same-day snapshot. Module memory wins (includes Show more days); session is the first screen.
 * @param {string} [todayKey]
 * @returns {object|null}
 */
export function peekEventFeedCache(todayKey = calendarDayKey(startOfLocalDay())) {
  if (
    memorySnapshot
    && memorySnapshot.dayKey === todayKey
    && Array.isArray(memorySnapshot.events)
  ) {
    return memorySnapshot;
  }
  const stored = readSession();
  if (stored && stored.dayKey === todayKey) {
    memorySnapshot = stored;
    return stored;
  }
  return null;
}

export function peekPublicEventById(rawId) {
  const want = rawId ? decodeURIComponent(String(rawId).trim()) : '';
  if (!want) return null;
  const snapshot = peekEventFeedCache();
  if (!snapshot) return null;
  return snapshot.events.find((event) => event?.id === want)
    || snapshot.featured.find((event) => event?.id === want)
    || null;
}

/** Full list stays in memory. sessionStorage keeps featured plus the first five days. */
export function commitEventFeedCache(snapshot) {
  const normalized = normalizeSnapshot(snapshot);
  if (!normalized) return;
  memorySnapshot = normalized;
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(EVENT_FEED_CACHE_KEY, JSON.stringify(toSessionSlice(normalized)));
  } catch {
    /* quota / private mode */
  }
}

function categoryCount(aggregate, label) {
  const raw = aggregate?.byCategory?.[label];
  return typeof raw === 'number' ? raw : 0;
}

function mismatchedLoadedDayKeys(snapshot, nextAggregate) {
  const from = parseCalendarDayKey(snapshot.dayKey);
  const to = parseCalendarDayKey(snapshot.loadedThrough);
  if (!from || !to) return [];
  const loaded = countLoadedByDay(snapshot.events);
  const byDay = nextAggregate?.byDay || {};
  const keys = [];
  for (let day = startOfLocalDay(from); day < to; day = addCalendarDays(day, 1)) {
    const key = calendarDayKey(day);
    const expected = byDay[key] || 0;
    const have = loaded[key] || 0;
    if (have !== expected) keys.push(key);
  }
  return keys;
}

/**
 * Decide which event queries a same-day snapshot still needs after a fresh aggregate read.
 * @returns {{ unchanged: boolean, dayKeys: string[], refetchFeatured: boolean, refetchWindow: boolean }}
 */
export function planFeedRefresh(snapshot, nextAggregate) {
  const prevAt = updatedAtMillis(snapshot?.aggregate?.updatedAt);
  const nextAt = updatedAtMillis(nextAggregate?.updatedAt);
  if (prevAt != null && nextAt != null && prevAt === nextAt) {
    return { unchanged: true, dayKeys: [], refetchFeatured: false, refetchWindow: false };
  }
  const dayKeys = mismatchedLoadedDayKeys(snapshot, nextAggregate);
  const refetchFeatured = categoryCount(snapshot?.aggregate, SAVE_THE_DATE_LABEL)
    !== categoryCount(nextAggregate, SAVE_THE_DATE_LABEL);
  if (dayKeys.length === 0 && !refetchFeatured) {
    return { unchanged: false, dayKeys: [], refetchFeatured: true, refetchWindow: true };
  }
  return { unchanged: false, dayKeys, refetchFeatured, refetchWindow: false };
}
